import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { ListIcon } from "@phosphor-icons/react/List";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Menu, MenuSeparator } from "@freightsimple/ui-core/Menu/Menu";
import { NavBarGetQuotesButton } from "../../Buttons/NavBarGetQuotesButton";
import {
  DEVELOPERS_PATHS,
  FINANCIAL_PATHS,
  MANAGE_COMPANY_PATHS,
  NAV_GAP,
  NavBarItem,
  NavBarMenuTrigger,
  SHIPMENTS_PATHS,
  type NavEntry,
  type TopNavBarProps,
} from "./navBarTopBarPrimitives";
import {
  developersItems,
  financialItems,
  manageCompanyItems,
  shipmentsItems,
} from "./navBarTopBarItems";

/**
 * Priority+ primary nav: renders as many items inline as fit, and moves the
 * rest (right to left) into a "More" menu. Widths are measured from a hidden
 * layer and recomputed on resize, so items collapse one at a time rather than
 * all at once.
 */
export function NavBarOverflowNav({
  activePath,
  onNavigate,
  onHelpCenter,
  onReferralCode,
  showDrafts,
  showDevelopers,
  quotesCtaInMenu,
}: Pick<
  TopNavBarProps,
  | "activePath"
  | "onNavigate"
  | "onHelpCenter"
  | "onReferralCode"
  | "showDrafts"
  | "showDevelopers"
> & {
  /** On a phone the quotes CTA moves off the bar and into this menu. */
  quotesCtaInMenu: boolean;
}) {
  const entries: NavEntry[] = [
    {
      key: "shipments",
      kind: "menu",
      title: "Shipments",
      activePaths: SHIPMENTS_PATHS,
    },
    {
      key: "manage",
      kind: "menu",
      title: "Manage Company",
      activePaths: MANAGE_COMPANY_PATHS,
    },
    {
      key: "financial",
      kind: "menu",
      title: "Financial",
      activePaths: FINANCIAL_PATHS,
    },
    ...(showDevelopers
      ? [
          {
            key: "developers",
            kind: "menu",
            title: "Developers",
            activePaths: DEVELOPERS_PATHS,
          } satisfies NavEntry,
        ]
      : []),
    { key: "help", kind: "external", title: "Help Center" },
  ];

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(entries.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    function recompute() {
      const available = container!.offsetWidth;
      const children = Array.from(measure!.children) as HTMLElement[];
      if (children.length === 0) {
        return;
      }
      // Last measured child is the "More" button; the rest are entry triggers.
      const moreWidth = children[children.length - 1]!.offsetWidth;
      const widths = children.slice(0, -1).map((el) => el.offsetWidth);

      const totalAll = widths.reduce((sum, w) => sum + w + NAV_GAP, 0);
      if (totalAll <= available) {
        setVisibleCount(widths.length);
        return;
      }

      let used = moreWidth + NAV_GAP;
      let count = 0;
      for (const w of widths) {
        if (used + w + NAV_GAP <= available) {
          used += w + NAV_GAP;
          count += 1;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    }

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [showDrafts, showDevelopers]);

  /** The rows of a dropdown group, shared by the inline menu and the "More" menu. */
  function menuItems(key: string) {
    switch (key) {
      case "manage":
        return manageCompanyItems(onNavigate, onReferralCode);
      case "financial":
        return financialItems(onNavigate);
      case "developers":
        return developersItems(onNavigate);
      default:
        return shipmentsItems(onNavigate, showDrafts);
    }
  }

  function entryOnClick(entry: NavEntry) {
    return entry.kind === "external"
      ? onHelpCenter
      : () => onNavigate(entry.path!);
  }

  /** The full inline rendering (dropdowns open real menus). */
  function inlineEntry(entry: NavEntry) {
    if (entry.kind === "menu") {
      return (
        <Menu
          key={entry.key}
          width="wide"
          // Opens directly below the nav item. The default `end` hangs a wide
          // menu far off to the left of a narrow trigger like "Financial".
          align="start"
          trigger={
            <NavBarMenuTrigger
              title={entry.title}
              active={entry.activePaths!.includes(activePath)}
              testId={`subnav-${entry.title}`}
            />
          }
        >
          {menuItems(entry.key)}
        </Menu>
      );
    }
    return (
      <NavBarItem
        key={entry.key}
        title={entry.title}
        active={entry.kind === "link" ? activePath === entry.path : false}
        onClick={entryOnClick(entry)}
        testId={`subnav-${entry.title}`}
      />
    );
  }

  /** Just the trigger at natural width, for the hidden measurement layer.
   *  No testid — that would duplicate the visible nav's hooks. */
  function measureEntry(entry: NavEntry) {
    if (entry.kind === "menu") {
      return (
        <NavBarMenuTrigger
          key={entry.key}
          title={entry.title}
          active={false}
          testId={undefined}
        />
      );
    }
    return (
      <NavBarItem
        key={entry.key}
        title={entry.title}
        active={false}
        onClick={() => {}}
        testId={undefined}
      />
    );
  }

  const visible = entries.slice(0, visibleCount);
  const overflow = entries.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      // min-w-10 (not 0): always leave room for the collapsed "More" ☰
      // trigger, otherwise the right cluster overlaps it on tiny screens.
      className="relative flex min-w-10 flex-1 items-center gap-1"
    >
      {/* Hidden measurement layer — every trigger + the "More" button at their
          natural widths, so we can decide how many fit. Caged in a zero-size
          overflow-hidden wrapper: even invisible, an absolutely positioned
          strip still extends the page's scrollable area, which gave the whole
          app a horizontal scroll (and a white bar beside the full-bleed map)
          whenever the full item set was wider than the viewport. */}
      <div className="relative h-0 w-0 overflow-hidden" aria-hidden>
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute left-0 flex items-center gap-1"
        >
          {entries.map(measureEntry)}
          <span className="rounded-md p-1.5">
            <ListIcon className="h-5 w-5" weight="bold" />
          </span>
        </div>
      </div>

      {visible.map(inlineEntry)}
      {(overflow.length > 0 || quotesCtaInMenu) && (
        <Menu
          trigger={
            <button
              type="button"
              aria-label="More navigation"
              className="flex items-center rounded-md p-1.5 text-content-secondary transition-colors hover:bg-muted hover:text-foreground"
            >
              <ListIcon className="h-5 w-5" weight="bold" />
            </button>
          }
        >
          {quotesCtaInMenu && (
            <>
              <NavBarGetQuotesButton />
              {overflow.length > 0 && <MenuSeparator />}
            </>
          )}
          {overflow.map((entry, index) => (
            <Fragment key={entry.key}>
              {index > 0 && <MenuSeparator />}
              {entry.kind === "menu" ? (
                menuItems(entry.key)
              ) : (
                <Button
                  data-testid={`subnav-${entry.title}`}
                  onClick={entryOnClick(entry)}
                >
                  {entry.title}
                </Button>
              )}
            </Fragment>
          ))}
        </Menu>
      )}
    </div>
  );
}
